import React, {useEffect, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import {ToastContainer} from "react-toastify";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import {toastError, toastSuccess} from "../../../helpers/Toast";

const categoryLabels = {
  REGULATION: "Regulamin",
  DOCUMENT_TEMPLATE: "Wzór dokumentu",
  TRAINING_MATERIAL: "Materiał szkoleniowy",
  OTHER: "Inne"
};

const emptyItem = () => ({
  id: null,
  title: "",
  description: "",
  category: "REGULATION",
  kind: "FILE",
  externalUrl: "",
  published: true,
  sortOrder: 0,
  fileName: null
});

const errorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const EmployeeResourcesManager = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(emptyItem());
  const [file, setFile] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/employee-resources/manage");
      setItems(response.data || []);
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [axiosPrivate]);

  const openNew = () => {
    setCurrent(emptyItem());
    setFile(null);
    setShowModal(true);
  };

  const openEdit = item => {
    setCurrent({...item, externalUrl: item.externalUrl || ""});
    setFile(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!current.title.trim()) {
      toastError("Podaj nazwę materiału.");
      return;
    }
    if (current.kind === "FILE" && !current.fileName && !file) {
      toastError("Wybierz plik.");
      return;
    }
    if (current.kind === "LINK" && !current.externalUrl.trim()) {
      toastError("Podaj adres odnośnika.");
      return;
    }

    const data = new FormData();
    data.append("title", current.title.trim());
    data.append("description", current.description || "");
    data.append("category", current.category);
    data.append("kind", current.kind);
    data.append("externalUrl", current.kind === "LINK" ? current.externalUrl.trim() : "");
    data.append("published", String(Boolean(current.published)));
    data.append("sortOrder", String(Number(current.sortOrder || 0)));
    if (file) data.append("file", file);

    setSaving(true);
    try {
      if (current.id) {
        await axiosPrivate.put(`/employee-resources/manage/${current.id}`, data, {headers: {"Content-Type": "multipart/form-data"}});
      } else {
        await axiosPrivate.post("/employee-resources/manage", data, {headers: {"Content-Type": "multipart/form-data"}});
      }
      toastSuccess("Materiał został zapisany.");
      setShowModal(false);
      await load();
    } catch (error) {
      toastError(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async item => {
    if (!window.confirm(`Usunąć materiał „${item.title}”?`)) return;
    try {
      await axiosPrivate.delete(`/employee-resources/manage/${item.id}`);
      toastSuccess("Materiał został usunięty.");
      await load();
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  const download = async item => {
    try {
      const response = await axiosPrivate.get(`/employee-resources/manage/${item.id}/download`, {responseType: "blob"});
      const url = URL.createObjectURL(response.data);
      const anchor = window.document.createElement("a");
      anchor.href = url;
      anchor.download = item.fileName || item.title;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toastError(errorMessage(error));
    }
  };

  return (
    <div id="layoutSidenav_content">
      <ToastContainer />
      <div className="app-component-card-header shadow d-flex justify-content-between align-items-center">
        <div>Materiały dla pracowników</div>
        <Button size="sm" onClick={openNew}>Dodaj materiał</Button>
      </div>
      <div className="container-fluid px-4 py-4">
        <div className="card shadow-sm p-3">
          <p className="text-muted">Publikuj regulaminy, wzory dokumentów, materiały szkoleniowe i odnośniki widoczne w Panelu pracownika.</p>
          {loading ? <LoadingDataDiv /> : items.length === 0 ? (
            <div className="alert alert-light border mb-0">Nie opublikowano jeszcze żadnych materiałów.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead><tr><th>Kolejność</th><th>Nazwa</th><th>Kategoria</th><th>Typ</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id}>
                      <td>{item.sortOrder}</td>
                      <td><strong>{item.title}</strong>{item.description && <div className="small text-muted">{item.description}</div>}</td>
                      <td>{categoryLabels[item.category] || item.category}</td>
                      <td>{item.kind === "LINK" ? "Odnośnik" : item.fileName || "Plik"}</td>
                      <td><span className={`badge ${item.published ? "bg-success" : "bg-secondary"}`}>{item.published ? "Opublikowany" : "Ukryty"}</span></td>
                      <td className="text-end">
                        {item.kind === "FILE" && item.fileName && <Button size="sm" variant="outline-primary" className="me-2" onClick={() => download(item)}>Pobierz</Button>}
                        {item.kind === "LINK" && item.externalUrl && <Button size="sm" variant="outline-primary" className="me-2" onClick={() => window.open(item.externalUrl, "_blank", "noopener,noreferrer")}>Otwórz</Button>}
                        <Button size="sm" variant="outline-secondary" className="me-2" onClick={() => openEdit(item)}>Edytuj</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => remove(item)}>Usuń</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" centered size="lg">
        <Modal.Header closeButton><Modal.Title>{current.id ? "Edycja materiału" : "Nowy materiał"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>Nazwa *</Form.Label><Form.Control value={current.title} onChange={event => setCurrent(prev => ({...prev, title: event.target.value}))} /></Form.Group>
          <Form.Group className="mb-3"><Form.Label>Opis</Form.Label><Form.Control as="textarea" rows={3} value={current.description || ""} onChange={event => setCurrent(prev => ({...prev, description: event.target.value}))} /></Form.Group>
          <div className="row g-3 mb-3">
            <div className="col-md-6"><Form.Label>Kategoria</Form.Label><Form.Select value={current.category} onChange={event => setCurrent(prev => ({...prev, category: event.target.value}))}><option value="REGULATION">Regulamin</option><option value="DOCUMENT_TEMPLATE">Wzór dokumentu</option><option value="TRAINING_MATERIAL">Materiał szkoleniowy</option><option value="OTHER">Inne</option></Form.Select></div>
            <div className="col-md-3"><Form.Label>Typ</Form.Label><Form.Select value={current.kind} onChange={event => setCurrent(prev => ({...prev, kind: event.target.value}))}><option value="FILE">Plik</option><option value="LINK">Odnośnik</option></Form.Select></div>
            <div className="col-md-3"><Form.Label>Kolejność</Form.Label><Form.Control type="number" value={current.sortOrder ?? 0} onChange={event => setCurrent(prev => ({...prev, sortOrder: event.target.value}))} /></div>
          </div>
          {current.kind === "FILE" ? (
            <Form.Group className="mb-3"><Form.Label>Plik{current.id && current.fileName ? " – pozostaw puste, aby zachować obecny" : " *"}</Form.Label><Form.Control type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.jpg,.jpeg,.png" onChange={event => setFile(event.target.files?.[0] || null)} />{current.fileName && <Form.Text>Aktualny plik: {current.fileName}</Form.Text>}</Form.Group>
          ) : (
            <Form.Group className="mb-3"><Form.Label>Adres URL *</Form.Label><Form.Control type="url" placeholder="https://..." value={current.externalUrl || ""} onChange={event => setCurrent(prev => ({...prev, externalUrl: event.target.value}))} /></Form.Group>
          )}
          <Form.Check type="switch" id="employee-resource-published" label="Widoczny dla pracowników" checked={Boolean(current.published)} onChange={event => setCurrent(prev => ({...prev, published: event.target.checked}))} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Anuluj</Button><Button onClick={save} disabled={saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeeResourcesManager;
